// src/components/DocumentManagement.tsx
import React, { useState, useEffect } from 'react';
import '../styles/DocumentManagement.css';
import Button from './Button';
import Modal from './Modal';
import LoadingIndicator from './LoadingIndicator';
import {
    uploadDocument,
    getDocumentDetail,
    deleteDocument,
    // queryDocument,
} from '../services/documentService';
import {
    subscriptionStatus,
    SubscriptionStatusResponse,
} from '../services/subscriptionService';
import { useDocumentList } from '../hooks/useDocumentList';
import { useDocumentStatus } from '../hooks/useDocumentStatus';
import { NotificationInfo } from '../types/NotificationInfo';

interface DocumentManagementProps {
    user: any;
    setNotification: (notification: NotificationInfo) => void;
}
/** ドキュメント処理ステータスの型 */
export interface DocumentStatus {
    status: string;
}

const DocumentManagement: React.FC<DocumentManagementProps> = ({
    user,
    setNotification,
}) => {
    // ---------- サブスク ----------
    const [subscription, setSubscription] =
        useState<SubscriptionStatusResponse['subscription']>(null);
    const [loadingSub, setLoadingSub] = useState(true);

    // ---------- アップロード ----------
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    // 受付後の “変換待ち” ドキュメント ID
    const [pendingDocId, setPendingDocId] = useState<number | null>(() => {
        const stored = localStorage.getItem('pendingDocId');
        return stored ? Number(stored) : null;
    });

    // ---------- 詳細閲覧 ----------
    const [viewContent, setViewContent] = useState<string | null>(null);
    const [loadingDocId, setLoadingDocId] = useState<number | null>(null);

    // ---------- クエリテスト ----------
    // const [queryText, setQueryText] = useState('');
    // const [queryResult, setQueryResult] = useState<any>(null);

    // ---------- 削除モーダル ----------
    const [modalOpen, setModalOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);

    // React Query Hooks
    const {
        data: documents = [],
        isLoading: isDocsLoading,
        refetch: refetchDocuments,
    } = useDocumentList();

    const { data: docStatus } = useDocumentStatus(pendingDocId!, {
        enabled: pendingDocId != null,
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
    });

    // ===========================
    // 1) サブスクリプション情報
    // ===========================
    useEffect(() => {
        (async () => {
            try {
                const res = await subscriptionStatus();
                setSubscription(res.data.subscription);
            } catch {
                setNotification({
                    message: 'サブスクリプション情報の取得に失敗しました。',
                    bgColor: '#dc3545',
                    textColor: '#fff',
                });
            } finally {
                setLoadingSub(false);
            }
        })();
    }, [setNotification]);

    // ===========================
    // 3) アップロード
    // ===========================
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) setSelectedFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        setUploading(true);
        try {
            const res = await uploadDocument(selectedFile);
            const { document_id } = res.data;
            setPendingDocId(document_id);
            localStorage.setItem('pendingDocId', String(document_id));
            setNotification({
                message: 'アップロードを受け付けました。完了までお待ちください…',
                bgColor: '#ffc107',
                textColor: '#000',
            });
        } catch (err: any) {
            const detail =
                err?.response?.data?.detail ?? "ドキュメントのアップロードに失敗しました。";
            setNotification({
                message: detail,
                bgColor: "#dc3545",
                textColor: "#fff",
            });
        } finally {
            setUploading(false);
            setSelectedFile(null);
        }
    };

    // ===================================
    // 4) 変換ステータス監視 (ポーリング)
    // ===================================
    useEffect(() => {
        if (!docStatus) return;
        if (docStatus.status === 'COMPLETED') {
            setPendingDocId(null);
            localStorage.removeItem('pendingDocId');
            setNotification({ message: 'アップロード完了！', bgColor: '#28a745' });
            refetchDocuments();
        }
        if (docStatus.status === 'FAILED') {
            setPendingDocId(null);
            localStorage.removeItem('pendingDocId');
            setNotification({ message: '変換失敗', bgColor: '#dc3545' });
        }
    }, [docStatus, refetchDocuments, setNotification]);

    // ===========================
    // 5) ドキュメント詳細
    // ===========================
    const handleViewContent = async (id: number) => {
        setLoadingDocId(id);
        try {
            const res = await getDocumentDetail(id);
            setViewContent(res.data.document_text);
        } catch {
            setNotification({
                message: 'ドキュメント取得に失敗しました。',
                bgColor: '#dc3545',
                textColor: '#fff',
            });
        } finally {
            setLoadingDocId(null);
        }
    };

    // ===========================
    // 6) 削除
    // ===========================
    const handleDeleteClick = (id: number) => {
        setDocToDelete(id);
        setModalOpen(true);
    };

    const confirmDelete = async () => {
        if (docToDelete == null) return;
        setDeleting(true);
        try {
            await deleteDocument(docToDelete);
            setNotification({
                message: 'ドキュメントが削除されました。',
                bgColor: '#28a745',
                textColor: '#fff',
            });
            // setQueryText('');
            // setQueryResult(null);
            setViewContent(null);
            await refetchDocuments();
        } catch {
            setNotification({
                message: 'ドキュメントの削除に失敗しました。',
                bgColor: '#dc3545',
                textColor: '#fff',
            });
        } finally {
            setDeleting(false);
            setModalOpen(false);
            setDocToDelete(null);
        }
    };

    // ===========================
    // 7) クエリテスト
    // ===========================
    // const handleQuerySubmit = async () => {
    //     if (!queryText) return;
    //     try {
    //         const res = await queryDocument(queryText);
    //         setQueryResult(res.data);
    //     } catch {
    //         setNotification({
    //             message: 'ドキュメントクエリに失敗しました。',
    //             bgColor: '#dc3545',
    //             textColor: '#fff',
    //         });
    //     }
    // };

    // ===========================
    // 8) UI レンダリング
    // ===========================
    if (!user || loadingSub || isDocsLoading) {
        return (
            <div className="loading-wrapper">
                <LoadingIndicator size={40} isFullPage />
            </div>
        );
    }
    if (!subscription || subscription.plan.name !== 'Premium') {
        return (
            <div className="document-section">
                <h2 className="section-title">ドキュメント管理</h2>
                <div className="document-unavailable">
                    このサービスは <strong>Premiumプラン</strong> 専用です。
                </div>
            </div>
        );
    }

    return (
        <div className="document-section">
            <h2 className="section-title">ドキュメント管理</h2>

            {/* アップロードフォーム */}
            {documents.length === 0 && !pendingDocId && (
                <>
                    <div className="document-intro">
                        <p>対応拡張子：".pdf" ".doc" ".docx" ".txt"</p>
                        <p>ドキュメントは、25,000文字以内としてください(超過時はエラーとなります)</p>
                        <p>ドキュメント内に画像がある場合、エラーとなる場合があります。</p>
                    </div>
                    <div className="document-upload">
                        <input
                            title="ドキュメントアップロード"
                            type="file"
                            accept=".pdf,.doc,.docx,.txt"
                            onChange={handleFileChange}
                        />
                        <Button
                            onClick={handleUpload}
                            disabled={uploading || !selectedFile}
                        >
                            {uploading ? 'アップロード中…' : 'アップロード'}
                        </Button>
                    </div>
                </>
            )}

            {/* 変換中インジケータ */}
            {pendingDocId && (
                <div className="document-uploading">
                    ドキュメントをアップロード中…<br></br>
                    完了まで数分かかる場合があります。
                </div>
            )}

            {/* ドキュメント操作パネル */}
            {documents.length > 0 && !pendingDocId && (
                <>
                    {/* クエリテスト */}
                    {/* <div className="document-query">
                        <h3>ドキュメント抽出テスト</h3>
                        <input
                            type="text"
                            value={queryText}
                            onChange={(e) => setQueryText(e.target.value)}
                            placeholder="質問を入力"
                        />
                        <Button onClick={handleQuerySubmit} disabled={!queryText}>
                            送信
                        </Button>
                        {queryResult && (
                            <div className="query-result">
                                <pre>{JSON.stringify(queryResult, null, 2)}</pre>
                            </div>
                        )}
                    </div> */}

                    {/* 一覧 */}
                    <div className="document-list">
                        {documents.map((doc) => (
                            <div className="document-item" key={doc.id}>
                                <span className="doc-name">{doc.document_name}</span>
                                <div className="document-actions">
                                    <Button
                                        onClick={() => handleViewContent(doc.id)}
                                        disabled={loadingDocId === doc.id}
                                    >
                                        {loadingDocId === doc.id ? '読み込み中…' : '内容を見る'}
                                    </Button>
                                    <Button
                                        bgColor="danger"
                                        onClick={() => handleDeleteClick(doc.id)}
                                    >
                                        削除
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* プレビュー */}
            {viewContent && (
                <div className="document-preview">
                    <div className="preview-header">
                        <h3>ファイルの内容</h3>
                        <Button onClick={() => setViewContent(null)}>閉じる</Button>
                    </div>
                    <pre>{viewContent}</pre>
                </div>
            )}

            {/* 削除確認モーダル */}
            {modalOpen && (
                <Modal isOpen onClose={() => setModalOpen(false)}>
                    <h3>削除の確認</h3>
                    <p>本当にこのドキュメントを削除しますか？</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                        <Button onClick={() => setModalOpen(false)} disabled={deleting}>
                            キャンセル
                        </Button>
                        <Button
                            bgColor="danger"
                            onClick={confirmDelete}
                            disabled={deleting}
                        >
                            {deleting ? '削除中…' : '削除'}
                        </Button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default DocumentManagement;
